import { useCallback, useEffect, useState } from 'react';
import { getPushKey, getPushStatus, subscribePush, unsubscribePush } from '../lib/api';
import { setPushEndpoint } from '../lib/push-device';
import { isDesktop } from '../lib/runtime';

/**
 * Web Push feliratkozas.
 *
 * Ez az ertesites utolso lancszeme: amikor ezen az eszkozon nem latszik az
 * alkalmazas. A ketto kozotti munkamegosztast lasd `lib/push-device.ts`.
 *
 * A desktop appban nincs ra szukseg: az mindig fut, es sajat rendszer-szintu
 * ertesitest hasznal.
 */

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on' | 'working';

const supported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * A VAPID kulcs base64url-bol az API altal vart byte-tombbe.
 *
 * A tombot kifejezetten `ArrayBuffer`-re epitjuk: a `pushManager.subscribe`
 * tipusa nem fogad `SharedArrayBuffer`-t, es a `Uint8Array.from` altalanosabb
 * tipust adna vissza.
 */
function toApplicationServerKey(base64Url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);

  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

/**
 * A service worker regisztralasa.
 *
 * Egy helyen, hogy ne fusson tobbszor. A `sw.js` szandekosan minimalis:
 * nem cache-el alkalmazas-adatot (lasd a fajl fejleceben).
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!supported() || isDesktop()) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    /*
      A feliratkozast MINDJART az indulaskor fel kell ismerni, nem csak a
      beallitasok megnyitasakor: ettol fugg, hogy a lap mutasson-e sajat
      ertesitest, es hogy a szerver tudja, melyik eszkozon latszik az ablak.
    */
    let subscription = await registration.pushManager.getSubscription().catch(() => null);

    /*
      ELVESZETT FELIRATKOZAS potlasa (K2).

      iOS-en a feliratkozas magatol is eltunhet (napok-hetek utan, vagy ha egy
      push nem jelenitett meg ertesitest). A felhasznalo ezt nem latja - csak
      annyit, hogy "nem jon ertesites". Ha az engedely megvan, de a
      feliratkozas nincs, csendben ujra feliratkozunk; engedelyt NEM kerunk
      (az iOS-en csak kattintasbol szabad).
    */
    if (
      !subscription &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      subscription = await resubscribeQuietly(registration);
    }
    setPushEndpoint(subscription?.endpoint ?? null);
    return registration;
  } catch {
    return null;
  }
}

/** Ujra-feliratkozas engedelykeres nelkul - csak ha az engedely mar megvan. */
async function resubscribeQuietly(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription | null> {
  try {
    const status = await getPushStatus();
    if (!status.enabled) return null;
    const publicKey = await getPushKey();
    if (!publicKey) return null;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toApplicationServerKey(publicKey),
    });
    await subscribePush(subscription.toJSON() as PushSubscriptionJSON);
    return subscription;
  } catch {
    // Nincs bejelentkezve, nincs halozat: a kovetkezo indulaskor ujra probaljuk.
    return null;
  }
}

export function usePushSubscription() {
  const [state, setState] = useState<PushState>('working');
  const [devices, setDevices] = useState(0);

  const refresh = useCallback(async () => {
    if (!supported() || isDesktop()) {
      setState('unsupported');
      return;
    }

    try {
      const status = await getPushStatus();
      setDevices(status.devices);

      if (!status.enabled) {
        // A szerveren nincs VAPID kulcs - meg sem kinaljuk a lehetoseget.
        setState('unavailable');
        return;
      }
      if (Notification.permission === 'denied') {
        setState('denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      setPushEndpoint(existing?.endpoint ?? null);
      setState(existing ? 'on' : 'off');
    } catch {
      setState('unavailable');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setState('working');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return;
      }

      const publicKey = await getPushKey();
      if (!publicKey) {
        setState('unavailable');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        // A bongeszok csak a "mindig latszik" modot engedik - nem hallgatolagos.
        userVisibleOnly: true,
        applicationServerKey: toApplicationServerKey(publicKey),
      });

      await subscribePush(subscription.toJSON() as PushSubscriptionJSON);
      await refresh();
    } catch {
      setState('off');
    }
  }, [refresh]);

  const disable = useCallback(async () => {
    setState('working');
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Eloszor a szervernel, utana helyben: ha a masodik bukik, akkor sem
        // marad a szerveren egy vegpont, amire mar nem varunk ertesitest.
        await unsubscribePush(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
        setPushEndpoint(null);
      }
      await refresh();
    } catch {
      setState('on');
    }
  }, [refresh]);

  return { state, devices, enable, disable, refresh };
}
